import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import DrawerViewSubmitQuestionnaire from '@app/src/components/Drawer/Views/DrawerViewSubmitQuestionnaire'
import DrawerViewSubmitShareQuestionnaire from '@app/src/components/Drawer/Views/DrawerViewSubmitShareQuestionnaire'
import type { Assertion } from '@app/src/types/reporting'
import { AnswerStatus, HistoryStatus, QuestionnaireTypeEnum } from '@app/src/types/resourceExplorer'
import { isValidCoordinate } from '@app/src/utils/coordinateParser'
import { SendOutlined } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Tooltip } from '@mui/material'
import React from 'react'
import type { UnpackNestedValue } from 'react-hook-form'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'
import useProviderQuestionnaire from '../../hooks/useProviderQuestionnaire'
import { useReport } from '../../ReportContext'
import type { FormData } from '../ProviderReport'

const scrollErrorIntoView = (key: string) => {
  const selectors = [
    `[name="${key}.answer"]`,
    `[name="${key}.selectedOptions"]`,
    `[name="${key}.uploadReferences.fileUpload"]`,
    `[name="${key}.comment"]`,
  ]

  const combinedSelectors = selectors.map(s => `${s}:not(:disabled)`).join(',')
  const input = document.querySelector<HTMLElement>(combinedSelectors)

  input?.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'start',
  })
}

function SubmitButton() {
  const { formatMessage } = useIntl()
  const {
    request,
    currentAccess,
    requestHistory,
    isRequestHistoryLoading,
    openPostSubmitFlow,
    savedResponseHasChangesFromLastSubmitted,
  } = useReport()
  const { submitResponse, isLoadingSubmitDraft } = useProviderQuestionnaire()

  const {
    getValues,
    reset,
    setError,
    clearErrors,
    formState: { isDirty },
  } = useFormContext<FormData>()
  const { openDrawer } = useDrawer()

  const isRegularQuestionnaire = request?.template?.questionnaireTemplateType === QuestionnaireTypeEnum.Regular

  const hasPriorSubmission = requestHistory?.some(history => history.status === HistoryStatus.Submitted) ?? false
  const hasAnyChanges = isDirty || savedResponseHasChangesFromLastSubmitted
  const isUnchangedUpdateOfSubmittedResponse = hasPriorSubmission && !hasAnyChanges

  const validateForm = (values: UnpackNestedValue<FormData>) => {
    clearErrors()
    let errorsExist = false
    Object.entries(values).forEach(([key, value]) => {
      const commentRequired = value.answerStatus === AnswerStatus.Other
      const questionSkipped =
        value.answerStatus !== AnswerStatus.Answered && (!commentRequired || (value.comment?.length ?? 0) >= 10)

      // For coordinates: ALL locations must have valid lat/long AND description
      const hasValidCoordinates =
        value.coordinates && value.coordinates.length > 0
          ? value.coordinates.every(
              coord =>
                coord.latitude !== undefined &&
                coord.longitude !== undefined &&
                coord.description !== undefined &&
                coord.description.trim() !== '' &&
                isValidCoordinate(coord.latitude, coord.longitude),
            )
          : false

      const isAnswered = Boolean(
        value.answer ||
        value.selectedOptions?.length ||
        value.uploadReferences?.originalFileName ||
        hasValidCoordinates ||
        questionSkipped,
      )

      if (isAnswered) return

      setError(key, {
        type: 'required',
        message: formatMessage({
          id: commentRequired ? 'form.validation.requiredMinLength' : 'form.validation.required',
        }),
      })

      setError(`${key}.skippedQuestionRequiredInfo`, {
        type: 'required',
      })

      if (errorsExist) return

      scrollErrorIntoView(key)
      errorsExist = true
    })

    return !errorsExist
  }

  const handleSubmit = async (values: UnpackNestedValue<FormData>, assertions: Assertion[]) => {
    await submitResponse(values, assertions, openPostSubmitFlow)
    reset(values)
  }

  const startSubmitFlow = () => {
    const values = getValues()
    if (!validateForm(values)) {
      return
    }
    openDrawer(
      isRegularQuestionnaire ? (
        <DrawerViewSubmitQuestionnaire
          isSharedWithTemplateAuthorizedOrgs={currentAccess?.isSharedWithTemplateAuthorizedOrgs}
          isStandardQuestionnaire={request?.template.isStandardReport}
          onSubmit={assertions => handleSubmit(values, assertions)}
        />
      ) : (
        <DrawerViewSubmitShareQuestionnaire
          request={request}
          currentAccess={currentAccess}
          onSubmit={assertions => handleSubmit(values, assertions)}
        />
      ),
    )
  }

  return (
    <>
      <Tooltip
        title={formatMessage({
          id: isUnchangedUpdateOfSubmittedResponse
            ? 'questionnaire.actions.noChangesToSubmit'
            : 'questionnaire.actions.requiredFields',
        })}
        arrow
        leaveDelay={400}
      >
        <Box display="inline-block">
          <LoadingButton
            startIcon={<SendOutlined />}
            fullWidth
            variant="contained"
            onClick={startSubmitFlow}
            loading={isLoadingSubmitDraft}
            disabled={isRequestHistoryLoading || isUnchangedUpdateOfSubmittedResponse}
          >
            {formatMessage({ id: 'questionnaire.actions.submit' })}
          </LoadingButton>
        </Box>
      </Tooltip>
    </>
  )
}

export default SubmitButton
